import { useState, useEffect, useRef } from "react";
import { useRule } from "../CreateRuleNew/RuleReducer";
import Button2 from "../../../Button/Button2";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import {
    GoogleMap,
    useLoadScript,
    Marker,
    DrawingManager,
    Circle,
} from "@react-google-maps/api";
import classes from "./RuleWizard.module.css";
import Button3 from "../../../Button/Button3";

const GEO_LIBRARIES = ["drawing"];



const GeofenceLocation = ({ onNext, onBack }) => {
    const [center, setCenter] = useState({ lat: 51.5074, lng: -0.1278 });
    const [circleData, setCircleData] = useState(null);
    const mapRef = useRef(null);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: "AIzaSyCLdJH2r1Lue1d_rZEDRQv1EYCShhRxETY",
        libraries: GEO_LIBRARIES,
    });

    useEffect(() => {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setCenter(loc);
                setCircleData({ ...loc, radius: 50 });
                if (mapRef.current) mapRef.current.panTo(loc);
            },
            () => {}
        );
    }, []);

    const onCircleComplete = (circle) => {
        circle.setMap(null);
        setCircleData({
            lat: circle.getCenter().lat(),
            lng: circle.getCenter().lng(),
            radius: circle.getRadius(),
        });
    };

    if (!isLoaded) return <div style={{ padding: "20px", color: "#9e9e9e" }}>Loading map...</div>;

    return (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "14px", minHeight: 0 }}>
            <GoogleMap
                center={center}
                zoom={17}
                onLoad={m => (mapRef.current = m)}
                mapContainerStyle={{ width: "100%", flex: 1, borderRadius: "8px", minHeight: "300px" }}
            >
                <Marker position={center} />
                <DrawingManager
                    onCircleComplete={onCircleComplete}
                    options={{
                        drawingControl: true,
                        drawingControlOptions: { drawingModes: ["circle"] },
                        circleOptions: { fillColor: "#FF0000", fillOpacity: 0.2, strokeWeight: 2, editable: true },
                    }}
                />
                {circleData && (
                    <Circle center={{ lat: circleData.lat, lng: circleData.lng }} radius={circleData.radius} />
                )}
            </GoogleMap>
            <div className={classes.navRow} style={{ marginTop: 0 }}>
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={onBack} />
                <Button
                    label="Next"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    severity="success"
                    disabled={!circleData}
                    onClick={onNext}
                />
            </div>
        </div>
    );
};

const TIMEFRAME_UNITS = [
    { label: "Seconds", value: 1000 },
    { label: "Minutes", value: 60000 },
    { label: "Hours",   value: 3600000 },
];

const WizardStepConditions = ({ pins, smartLocks, onNext, onBack }) => {
    const { state, dispatch } = useRule();

    const conditionPinStateLabel = (cond) => {
        const prefix = cond.is_absence_condition ? "NOT " : "";
        const onLabel  = state.isLock ? "closed" : (state.selectedRulePin.pinStatusOn  ?? "armed");
        const offLabel = state.isLock ? "open"   : (state.selectedRulePin.pinStatusOff ?? "disarmed");
        switch (cond.pin_state) {
            case "ARMED":    return `${prefix}${onLabel}`;
            case "DISARMED": return `${prefix}${offLabel}`;
            case "ANY":      return `${onLabel} or ${offLabel}`;
            case "PULSE":    return "pulsed";
            default:         return `${prefix}${cond.pin_state || "..."}`;
        }
    };

    const currentCondition = state.conditions[state.conditionEditIndex];

    const [pinState, setPinState] = useState(currentCondition?.pin_state || "");
    const [isAbsenceCondition, setIsAbsenceCondition] = useState(currentCondition?.is_absence_condition || false);
    const [disabled, setDisabled] = useState(!currentCondition?.pin_state || state.isPulse ? false : true);
    // const [showTimeframe, setShowTimeframe] = useState(false);
    // const [timeframeValue, setTimeframeValue] = useState("");
    // const [timeframeUnit, setTimeframeUnit] = useState(60000);
    const [showPinSelector, setShowPinSelector] = useState(false);

    // Re-init editor state when switching to a different condition
    useEffect(() => {
        const cond = state.conditions[state.conditionEditIndex];
        if (!cond) return;
        setPinState(cond.pin_state || "");
        setIsAbsenceCondition(cond.is_absence_condition || false);
        setDisabled(!(cond.pin_state) && !state.isPulse);
        setShowPinSelector(false);
    }, [state.conditionEditIndex]);

    // Sync local editor state into the condition object
    useEffect(() => {
        if (!pinState && !state.isPulse) return;
        const updated = state.conditions.map((cond, i) => {
            if (i !== state.conditionEditIndex) return cond;
            return {
                ...cond,
                pin_state: pinState || cond.pin_state,
                is_absence_condition: isAbsenceCondition,
            };
        });
        dispatch({ type: "SET_ADV_RULE_CONDITION", payload: updated });
    }, [pinState, isAbsenceCondition]);

    const handleStateSelected = (selection) => {
        dispatch({ type: "SET_INPUT_TYPE", payload: selection });
        dispatch({ type: "SET_INPUT_TYPE_COPY", payload: selection });
        switch (selection) {
            case 0: setPinState("ARMED"); setIsAbsenceCondition(false); break;
            case 1: setPinState("ANY"); setIsAbsenceCondition(false); break;
            case 2: setPinState("DISARMED"); setIsAbsenceCondition(false); break;
            case 3: setPinState("ARMED"); setIsAbsenceCondition(true);  break;
            case 4: setPinState("DISARMED"); setIsAbsenceCondition(true);  break;
            case 5: setLockLeftOpen(); setIsAbsenceCondition(false); break;
            default: break;
        }
        setDisabled(false);
    };

    const setLockLeftOpen = () => {
        const currentCond = state.conditions[state.conditionEditIndex];
        const firstCondition = { ...currentCond, pin_state: "ARMED", is_absence_condition: false };
        const secondCondition = { ...currentCond, pin_state: "DISARMED", is_absence_condition: true };

        const updatedConditions = [
            ...state.conditions.slice(0, state.conditionEditIndex),
            firstCondition,
            ...state.conditions.slice(state.conditionEditIndex + 1),
            secondCondition,
        ];

        dispatch({ type: "SET_ADV_RULE_CONDITION", payload: updatedConditions });
        setPinState("ARMED");
        setIsAbsenceCondition(false);
    }

    const renderStateText = (pinState) => {
        const newState = pinState
            switch (pinState) {
                case "armed": 
                    newState = state.isLock ? "closed" : state.selectedRulePin.pinStatusOn
                break;
                case "disarmed": 
                    newState = state.isLock ? "open" : state.selectedRulePin.pinStatusOff
                break;
                case "armed or disarmed":
                    newState = state.isLock ? "open or closed" : `${state.selectedRulePin.pinStatusOff} or ${state.selectedRulePin.pinStatusOn}`
                break;
            }

        return newState;
    }

    const handleAddAnotherPin = (pin) => {
        const newIndex = state.conditions.length;
        const condition = {
            condition_type: "STATE_CHECK",
            pin: pin.Pin,
            pin_name: pin.pinName,
            pin_state: pin.type === "PULSE" || pin.type === "VIRTUAL_PULSE" ? "PULSE" : pin.pin_state,
            trigger_method: pin.type === "PULSE" || pin.type === "VIRTUAL_PULSE" ? "PULSE" : "LATCH",
            trigger_method_type: "ANY",
            time_condition: "ANYTIME",
            days_of_week: ["0","1","2","3","4","5","6"],
            is_absence_condition: false,
        };
        dispatch({ type: "SET_ADV_RULE_CONDITION", payload: [...state.conditions, condition] });
        dispatch({ type: "SET_CONDITION_EDIT_INDEX", payload: newIndex });
        dispatch({ type: "SET_SELECTED_PIN", payload: pin });
        dispatch({ type: "SET_PIN_NUMBER", payload: pin.Pin });
        dispatch({ type: "SET_IS_LOCK", payload: smartLocks.some(l => parseInt(l.outputPin) === pin.Pin) });
        dispatch({ type: "SET_PIN_STATUS_ON", payload: pin.pinStatusOn });
        dispatch({ type: "SET_PIN_STATUS_OFF", payload: pin.pinStatusOff });
        dispatch({ type: "SET_PIN_NAME", payload: pin.pinName });
        dispatch({ type: "SET_IS_PULSE", payload: pin.type === "PULSE" || pin.type === "VIRTUAL_PULSE" });
        dispatch({ type: "SET_INPUT_TYPE", payload: -1 });
        dispatch({ type: "SET_INPUT_TYPE_COPY", payload: -1 });
    };

    if (state.isGeofence) {
        return (
            <>
                <div className={classes.contentHeader}>
                    <h2 className={classes.contentTitle}>Set geofence location</h2>
                    <p className={classes.contentSubtitle}>Draw a circle on the map to define the geofence zone.</p>
                </div>
                <GeofenceLocation onNext={onNext} onBack={onBack} />
            </>
        );
    }

    const canProceed = !disabled || state.isPulse;

    return (
        <>
            <div className={classes.contentHeader}>
                <h2 className={classes.contentTitle}>When does it trigger?</h2>
                <p className={classes.contentSubtitle}>Configure the condition(s) that must be met for the rule to fire.</p>
            </div>

            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "14px" }}>

                {/* Conditions chips + AND/OR + timeframe */}
                {state.conditions.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div className={classes.conditionChips}>
                            {state.conditions.map((cond, i) => (
                                <span
                                    key={i}
                                    className={`${classes.chip}`}
                                    style={{ cursor: "pointer" }}
                                >
                                {`When ${cond.pin_name ?? `Switch ${cond.pin}}`} is ${conditionPinStateLabel(cond)}`}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <Divider style={{ margin: "0" }} />

                {/* Condition editor */}
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <h4 style={{ color: "#6e6e6e", margin: 0, fontSize: "18px", marginBottom:"10px" }}>
                        When <strong>{state.pinName}</strong>:
                    </h4>
    
                    {state.isPulse ? (
                        <Button3
                            style={{
                                minWidth: "280px", minHeight: "40px",
                                backgroundColor: "var(--Primary-Color)", color: "white",
                                border: "2px solid lightblue",
                            }}
                        >
                            When {state.pinName} pulses
                        </Button3>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                            {[
                                { idx: 0, label: state.isLock ? "is closed" : `is ${state.pinStatusOn}` },
                                { idx: 1, label: state.isLock ? "is closed or open" : `is ${state.pinStatusOn} or ${state.pinStatusOff}` },
                                { idx: 2, label: state.isLock ? "is open" : `is ${state.pinStatusOff}` },
                                { idx: 3, label: state.isLock ? `has NOT been closed` : `is NOT ${state.pinStatusOff}` , advOnly: true },
                                { idx: 4,label: state.isLock ? `has NOT been opened` : `is NOT ${state.pinStatusOn}`, advOnly: true },
                                state.isLock && { idx: 5, label: "Has been left open", advOnly: true }
                            ]
                                .filter(b => b && (!b.advOnly || state.supportsAdvRules))
                                .map(({ idx, label }) => (
                                    <Button3
                                        key={idx}
                                        style={{
                                            minWidth: "280px", minHeight: "40px",
                                            backgroundColor: state.inputType === idx ? "var(--Primary-Color)" : undefined,
                                            color: state.inputType === idx ? "white" : undefined,
                                            border: state.inputType === idx ? "2px solid lightblue" : undefined,
                                            whiteSpace: "normal",
                                        }}
                                        onClick={() => handleStateSelected(idx)}
                                    >
                                        {label}
                                    </Button3>
                                ))}
                        </div>
                    )}
                </div>
                <br/>
                {/* Add another condition */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {!showPinSelector ? (
                        <Button
                            label="Add another condition"
                            icon="pi pi-plus"
                            size="small"
                            severity="info"
                            rounded
                            
                            onClick={() => setShowPinSelector(true)}
                        />
                    ) : (
                    <div className={classes.pinSelectorPanel}>
                        <p style={{ color: "#6e6e6e", fontSize: "13px", margin: "0 0 10px 0", fontWeight: 600 }}>
                            Choose a pin for the next condition:
                        </p>
                        <div className={classes.pinGrid}>
                            {pins.map((pin, i) => (
                                <Button3
                                    key={i}
                                    style={{ minWidth: "130px", minHeight: "36px", fontSize: "13px" }}
                                    onClick={() => handleAddAnotherPin(pin)}
                                >
                                    {pin.pinName}
                                </Button3>
                            ))}
                        </div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            size="small"
                            severity="secondary"
                            text
                            style={{ marginTop: "8px" }}
                            onClick={() => setShowPinSelector(false)}
                        />
                    </div>
                    )}
                </div>
            </div>

            <div className={classes.navRow}>
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={onBack} />
                <Button
                    label="Next"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    severity="success"
                    disabled={!canProceed}
                    onClick={onNext}
                />
            </div>
        </>
    );
};

export default WizardStepConditions;
